import { useEffect, useMemo, useState } from 'react';
import Env from '../env/Env';

interface DowntimeInfo {
  isDowntimeActive: boolean;
  startUtc: Date;
  endUtc: Date;
  startLabel: string;
  endLabel: string;
}

/**
 * Hook to manage downtime logic
 * Parses environment variables as London time and checks if downtime is currently active
 */
export function useDowntime(): DowntimeInfo {
  const [nowTs, setNowTs] = useState<number>(Date.now());
  const [serverDriftMs, setServerDriftMs] = useState<number>(0);

  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    // Sync with server time using Date header from a HEAD request
    (async () => {
      try {
        const res = await fetch('/robots.txt', { method: 'HEAD', cache: 'no-store' });
        const dateHeader = res.headers.get('date') || res.headers.get('Date');
        if (dateHeader) {
          const serverNow = new Date(dateHeader).getTime();
          const clientNow = Date.now();
          setServerDriftMs(serverNow - clientNow);
        }
      } catch {
        // ignore drift if network blocked; fallback to client time
      }
    })();
  }, []);

  const downtimeInfo = useMemo((): DowntimeInfo => {
    const url = new URL(window.location.href);
    const force = url.searchParams.get('forceDowntime');
    if (force === '1' || force === 'true') {
      const nowAdj = new Date(nowTs + serverDriftMs);
      const startForce = new Date(nowAdj.getTime() - 5 * 60 * 1000);
      const endForce = new Date(nowAdj.getTime() + 2 * 60 * 60 * 1000);
      return { 
        isDowntimeActive: true, 
        startUtc: startForce, 
        endUtc: endForce, 
        startLabel: startForce.toLocaleString('en-GB'), 
        endLabel: endForce.toLocaleString('en-GB') 
      };
    }

    // Only show downtime if environment variables are set
    if (!Env.DOWNTIME_START || !Env.DOWNTIME_END) {
      return { 
        isDowntimeActive: false, 
        startUtc: new Date(), 
        endUtc: new Date(), 
        startLabel: '', 
        endLabel: '' 
      };
    }

    const nowAdj = new Date(nowTs + serverDriftMs);
    
    // Parse environment variable times as London time
    // Helper function to parse a date string as London time and convert to UTC
    const parseLondonTime = (dateStr: string): Date => {
      // Parse the ISO string (e.g., "2024-01-20T10:00:00")
      const parts = dateStr.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (!parts) {
        throw new Error(`Invalid date format: ${dateStr}`);
      }
      const [, year, month, day, hour, minute, second] = parts.map(Number);
      
      // Strategy: Find the UTC time that corresponds to the given London time
      // We'll use binary search or iterative approach to find the correct UTC time
      // Start with a candidate UTC time (treating input as UTC initially)
      let candidateUtc = new Date(Date.UTC(year, month - 1, day, hour, minute, second || 0));
      
      // Get what London time this UTC candidate represents
      const londonFormatter = new Intl.DateTimeFormat('sv-SE', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      
      // Iteratively adjust until we find the correct UTC time
      for (let i = 0; i < 10; i++) {
        const londonStr = londonFormatter.format(candidateUtc);
        const [londonYear, londonMonth, londonDay, londonHour, londonMinute, londonSecond] = 
          londonStr.match(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/)?.slice(1).map(Number) || [];
        
        if (!londonYear) break;
        
        // Calculate difference in milliseconds
        const desiredLondonTime = new Date(Date.UTC(year, month - 1, day, hour, minute, second || 0));
        const actualLondonTime = new Date(Date.UTC(londonYear, londonMonth - 1, londonDay, londonHour, londonMinute, londonSecond));
        const diffMs = desiredLondonTime.getTime() - actualLondonTime.getTime();
        
        if (Math.abs(diffMs) < 1000) break; // Close enough (within 1 second)
        
        candidateUtc = new Date(candidateUtc.getTime() + diffMs);
      }
      
      return candidateUtc;
    };

    const start = parseLondonTime(Env.DOWNTIME_START);
    const end = parseLondonTime(Env.DOWNTIME_END);

    // Check if downtime is active: current time must be >= start and < end
    // This ensures downtime stops showing after end time passes
    const active = nowAdj >= start && nowAdj < end;

    // Format labels in user's local timezone
    const startLabel = new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(start);

    const endLabel = new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(end);

    return { isDowntimeActive: active, startUtc: start, endUtc: end, startLabel, endLabel };
  }, [nowTs, serverDriftMs]);

  return downtimeInfo;
}

